import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Plus } from "lucide-react";
import { ActorStamp } from "@/components/shared/ActorStamp";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { DetailHeader } from "@/components/shared/PageChrome";
import { BriefEvidenceCard } from "@/components/operational/EvidenceCards";
import { OfferComparison, StageStepper } from "@/components/enquiries/OfferComparison";
import { LoadingState, ErrorState } from "@/components/shared/States";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AsyncPaginatedSelect, directoryFetcher } from "@/components/shared/AsyncPaginatedSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ResponseTargetPanel } from "@/components/enquiries/ResponseTargetPanel";
import { AwaitingInfoPanel } from "@/components/enquiries/AwaitingInfoPanel";
import { IntakePanel } from "@/components/enquiries/IntakePanel";
import { OptionsLogPanel } from "@/components/enquiries/OptionsLogPanel";
import { DeclineOfferDialog, OfferFeedbackSummary } from "@/components/offers/DeclineOfferDialog";
import { OfferInternalNotes } from "@/components/offers/OfferInternalNotes";
import { UrgentChip, UrgentPanel } from "@/components/client/UrgentMarker";
import { TermsFields, EMPTY_TERMS, termsPayload, termsComplete, termsFromOffer } from "@/components/offers/OfferTerms";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-jigsaw-muted">{label}</p>
      <p className="mt-1 text-sm text-jigsaw-navy">{value || "—"}</p>
    </div>
  );
}

export default function EnquiryDetail() {
  const { id } = useParams();
  const [enquiry, setEnquiry] = useState(null);
  const [error, setError] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [assignStaffId, setAssignStaffId] = useState("");
  const [reqOpen, setReqOpen] = useState(false);
  const [reqForm, setReqForm] = useState({ requirement_type: "", description: "", is_mandatory: false });
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerForm, setOfferForm] = useState({ supplier_id: "", property_id: "", notes: "", ...EMPTY_TERMS });
  const [editOffer, setEditOffer] = useState(null);  // { id, version, presented, form }
  const [supplierProperties, setSupplierProperties] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get(`/enquiries/${id}`).then((r) => setEnquiry(r.data)).catch(() => setError(true));

  useEffect(() => {
    load();
    api.get("/people/staff-users").then((r) => setStaffOptions(r.data.items)).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post(`/enquiries/${id}/complete`);
      toast.success(`Booking ${data.booking_reference} confirmed`);
      navigate(`/control-centre/bookings/${data.booking_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete this enquiry.");
    } finally {
      setCompleting(false);
    }
  };

  const assign = async () => {
    try {
      await api.post(`/enquiries/${id}/assign`, { staff_user_id: assignStaffId });
      toast.success("Enquiry assigned");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign"); }
  };

  const addRequirement = async () => {
    try {
      await api.post(`/enquiries/${id}/requirements`, reqForm);
      toast.success("Requirement added");
      setReqOpen(false);
      setReqForm({ requirement_type: "", description: "", is_mandatory: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add requirement"); }
  };

  const onSupplierChange = async (supplierId) => {
    setOfferForm({ ...offerForm, supplier_id: supplierId, property_id: "" });
    if (!supplierId) { setSupplierProperties([]); return; }
    try {
      const { data } = await api.get(`/directory/suppliers/${supplierId}`);
      setSupplierProperties(data.properties);
    } catch { setSupplierProperties([]); }
  };

  const recordOffer = async () => {
    try {
      await api.post(`/enquiries/${id}/offers`, { supplier_id: offerForm.supplier_id, property_id: offerForm.property_id || null, notes: offerForm.notes || null, ...termsPayload(offerForm) });
      toast.success("Supplier offer recorded");
      setOfferOpen(false);
      setOfferForm({ supplier_id: "", property_id: "", notes: "", ...EMPTY_TERMS });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record offer"); }
  };

  const openEdit = (o) => setEditOffer({ id: o.id, version: o.terms?.version || 1, presented: o.presented, form: termsFromOffer(o.terms) });
  const saveEdit = async () => {
    try {
      const r = await api.patch(`/enquiries/${id}/offers/${editOffer.id}`, termsPayload(editOffer.form));
      const msg = { IN_PLACE: "Offer terms updated", NEW_VERSION: `Version ${r.data.version} drafted - present it to replace the version the client has seen`, UNCHANGED: "No changes to save", NOTES_ONLY: "Notes updated" }[r.data.mode] || "Saved";
      toast.success(msg); setEditOffer(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update the offer"); }
  };
  const presentOffer = async (offerId) => {
    try {
      await api.post(`/enquiries/${id}/offers/${offerId}/present`);
      toast.success("Offer presented to client");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not present offer"); }
  };

  const [declining, setDeclining] = useState(null);
  const decideOnBehalf = async (clientOfferId, decision, extra = {}) => {
    if (decision === "DECLINED" && !extra.decline_reason) { setDeclining(clientOfferId); return; }
    try {
      await api.post(`/enquiries/${id}/client-offers/${clientOfferId}/decide`, { decision, notes: "Recorded by staff on the client's behalf", ...extra });
      toast.success(`Offer ${decision.toLowerCase()}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record decision"); if (decision === "DECLINED") throw e; }
  };

  if (error) return <ErrorState testId="enquiry-detail-error" />;
  if (!enquiry) return <LoadingState testId="enquiry-detail-loading" />;

  const canComplete = !["COMPLETED", "CANCELLED"].includes(enquiry.status) && !enquiry.booking_id;
  const isTerminal = ["COMPLETED", "CANCELLED"].includes(enquiry.status) || !!enquiry.booking_id;

  return (
    <div data-testid="enquiry-detail-page" className="space-y-5">
      <DetailHeader
        testId="enquiry-detail-header"
        titleTestId="enquiry-reference-heading"
        crumbs={[{ label: "Enquiries", to: "/control-centre/enquiries" }, { label: enquiry.jigsaw_reference }]}
        reference={enquiry.jigsaw_reference}
        chips={<><StatusBadge status={enquiry.status} /><StatusBadge status={enquiry.priority} /><UrgentChip urgent={enquiry.client_urgent} testId="enquiry-urgent-chip" />{enquiry.source && <span className="rounded-full border border-jigsaw-border px-2.5 py-0.5 text-[12px] text-jigsaw-muted">{enquiry.source}</span>}</>}
        title={<>{enquiry.jigsaw_reference} &middot; {enquiry.client?.name}{enquiry.location_text ? ` — ${enquiry.location_text}` : ""}</>}
        meta={[
          { label: "Client organisation", value: enquiry.client?.name },
          { label: "Guest / lead", value: enquiry.guest_lead_name },
          { label: "Postcode", value: enquiry.postcode, mono: true },
          ...(enquiry.borough !== undefined ? [{ label: "Borough", value: enquiry.borough_label || "Not recorded" }] : []),
          { label: "Arrival", value: enquiry.arrival_date },
          { label: "Departure", value: enquiry.departure_date },
          { label: "Rooms / guests", value: `${enquiry.rooms_required ?? "—"} / ${enquiry.guests_count ?? "—"}` },
          { label: "Type · work status", value: `${enquiry.enquiry_type === "MOVE" ? "Move" : "New enquiry"} · ${String(enquiry.work_status || "").toLowerCase().replace(/_/g, " ")}` },
          ...(enquiry.linked_booking_reference ? [{ label: "Moved from", value: enquiry.linked_booking_reference, mono: true }] : []),
          ...(enquiry.lost_reason ? [{ label: "Lost reason", value: `${enquiry.lost_reason.toLowerCase().replace(/_/g, " ")}${enquiry.lost_note ? ` — ${enquiry.lost_note}` : ""}` }] : []),
        ]}
        actions={<>
          <div className="flex items-center gap-2">
            <Select value={assignStaffId} onValueChange={setAssignStaffId}>
              <SelectTrigger data-testid="assign-staff-select" className="h-9 w-52"><SelectValue placeholder={enquiry.assigned_staff?.name ? `Assigned: ${enquiry.assigned_staff.name}` : "Assign staff member"} /></SelectTrigger>
              <SelectContent>{staffOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="assign-staff-button" size="sm" variant="outline" disabled={!assignStaffId} onClick={assign}>Assign</Button>
          </div>
          <button onClick={() => navigate("/control-centre/enquiries")} data-testid="enquiry-back-button" className="sr-only">Back to Enquiries</button>
          {enquiry.booking_id ? (
            <Button variant="outline" size="sm" data-testid="enquiry-view-booking-button" onClick={() => navigate(`/control-centre/bookings/${enquiry.booking_id}`)}>View Booking</Button>
          ) : canComplete ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" data-testid="enquiry-complete-button" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark"><CheckCircle2 className="mr-2 h-4 w-4" /> Complete Enquiry</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Complete this enquiry?</AlertDialogTitle>
                  <AlertDialogDescription>This creates exactly one confirmed Booking from {enquiry.jigsaw_reference}. This action is transactional and cannot create duplicate bookings, even if retried.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="enquiry-complete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="enquiry-complete-confirm" disabled={completing} onClick={handleComplete}>{completing ? "Completing..." : "Complete Enquiry"}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : null}
        </>}
      />
      <StageStepper status={enquiry.status} />
      <BriefEvidenceCard enquiry={enquiry} onSaved={load} />

      <ResponseTargetPanel enquiry={enquiry} onChanged={load} />
      {enquiry.intake !== undefined && <IntakePanel enquiry={enquiry} onChanged={load} />}
      <Tabs defaultValue="overview">
        <TabsList data-testid="enquiry-tabs">
          <TabsTrigger value="overview" data-testid="enquiry-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="sourcing" data-testid="enquiry-tab-sourcing">Sourcing &amp; Offers</TabsTrigger>
          <TabsTrigger value="activity" data-testid="enquiry-tab-activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <Card className="border-jigsaw-border">
            <CardHeader><CardTitle className="text-base text-jigsaw-navy">Request Details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-5 md:grid-cols-4">
              <Field label="Client" value={enquiry.client?.name} />
              <Field label="Guest / Lead name" value={enquiry.guest_lead_name} />
              <Field label="Location" value={enquiry.location_text} />
              <Field label="Postcode" value={enquiry.postcode} />
              <Field label="Arrival" value={enquiry.arrival_date} />
              <Field label="Departure" value={enquiry.departure_date} />
              <Field label="Rooms required" value={enquiry.rooms_required} />
              <Field label="Guests" value={enquiry.guests_count} />
              <Field label="Assigned staff" value={enquiry.assigned_staff?.name} />
              <Field label="Source" value={enquiry.source} />
              <Field label="Legacy reference" value={enquiry.legacy_reference} />
            </CardContent>
          </Card>

          <UrgentPanel urgent={enquiry.client_urgent} testId="staff-urgent-panel" canAcknowledge onAcknowledge={async () => { try { await api.post(`/enquiries/${id}/urgent/acknowledge`); toast.success("Urgency marker acknowledged"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not acknowledge"); } }} />
          <AwaitingInfoPanel enquiry={enquiry} onChanged={load} disabled={isTerminal} />

          {enquiry.urgency_notes && (
            <Card className="border-jigsaw-border">
              <CardHeader><CardTitle className="text-base text-jigsaw-navy">Urgency / Notes</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-jigsaw-navy">{enquiry.urgency_notes}</p></CardContent>
            </Card>
          )}

          {enquiry.requirements.length > 0 && (
            <Card className="border-jigsaw-border">
              <CardHeader><CardTitle className="text-base text-jigsaw-navy">Requirements</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {enquiry.requirements.map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-md border border-jigsaw-border px-3 py-2 text-sm">
                    <span className="text-jigsaw-navy">{r.description}</span>
                    <span className="text-xs text-jigsaw-muted">{r.requirement_type}{r.is_mandatory ? " · Mandatory" : ""}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Dialog open={reqOpen} onOpenChange={setReqOpen}>
            <DialogTrigger asChild><Button data-testid="add-requirement-button" size="sm" variant="outline"><Plus className="mr-1 h-3.5 w-3.5" /> Add Requirement</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add requirement</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Type</Label><Input data-testid="requirement-type-input" value={reqForm.requirement_type} onChange={(e) => setReqForm({ ...reqForm, requirement_type: e.target.value })} placeholder="e.g. ACCESSIBILITY" /></div>
                <div><Label>Description</Label><Input data-testid="requirement-description-input" value={reqForm.description} onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="requirement-save-button" onClick={addRequirement} disabled={!reqForm.description}>Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="sourcing" className="space-y-6">
          <Card className="border-jigsaw-border">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-base text-jigsaw-navy">Supplier Offers</CardTitle>
              {isTerminal ? (
                <span data-testid="record-offer-disabled-reason" className="rounded-md border border-jigsaw-border bg-jigsaw-bg px-2.5 py-1 text-[12px] text-jigsaw-muted">Enquiry is {enquiry.status.toLowerCase()} - offers can no longer be recorded</span>
              ) : (
              <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
                <DialogTrigger asChild><Button data-testid="record-offer-button" size="sm" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark"><Plus className="mr-1 h-3.5 w-3.5" /> Record Offer</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Record a supplier offer</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Supplier</Label>
                      <AsyncPaginatedSelect testId="offer-supplier-select" value={offerForm.supplier_id} placeholder="Select supplier" onChange={onSupplierChange}
                        fetchPage={directoryFetcher("/directory/suppliers")} getSub={(s) => [s.region, s.status].filter(Boolean).join(" · ")} />
                    </div>
                    <div>
                      <Label>Property (optional)</Label>
                      <Select value={offerForm.property_id || "none"} onValueChange={(v) => setOfferForm({ ...offerForm, property_id: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="offer-property-select"><SelectValue placeholder="Not specified" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">Not specified</SelectItem>{supplierProperties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <TermsFields form={offerForm} setForm={setOfferForm} idPrefix="offer" />
                    <div><Label>Internal notes <span className="text-jigsaw-muted">(never shown to the client)</span></Label><Input data-testid="offer-notes-input" value={offerForm.notes} onChange={(e) => setOfferForm({ ...offerForm, notes: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button data-testid="offer-record-save-button" onClick={recordOffer} disabled={!offerForm.supplier_id || !termsComplete(offerForm)}>Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              )}
              <Dialog open={!!editOffer} onOpenChange={(v) => !v && setEditOffer(null)}>
                <DialogContent>
                  <DialogHeader><DialogTitle>Edit offer terms (v{editOffer?.version})</DialogTitle></DialogHeader>
                  {editOffer?.presented && <p data-testid="offer-edit-version-notice" className="rounded-md border border-jigsaw-warning/30 bg-jigsaw-warningBg px-3 py-2 text-[12px] text-jigsaw-warning">The client has seen this version. Saving creates a new draft version; presenting it supersedes v{editOffer.version} and any acceptance of it, and the client must accept again.</p>}
                  {editOffer && <div className="space-y-3"><TermsFields form={editOffer.form} setForm={(f) => setEditOffer({ ...editOffer, form: f })} idPrefix="offer-edit" /></div>}
                  <DialogFooter><Button data-testid="offer-edit-save-button" onClick={saveEdit} disabled={!editOffer || !termsComplete(editOffer.form)}>{editOffer?.presented ? "Save as new version" : "Save"}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <OfferComparison offers={enquiry.supplier_offers} clientOffers={enquiry.client_offers} onPresent={presentOffer} onEdit={openEdit} readOnly={isTerminal} nights={enquiry.arrival_date && enquiry.departure_date ? Math.round((new Date(enquiry.departure_date) - new Date(enquiry.arrival_date)) / 86400000) : null} />
            </CardContent>
          </Card>

          <Card className="border-jigsaw-border">
            <CardHeader><CardTitle className="text-base text-jigsaw-navy">Client Offers</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {enquiry.client_offers.length === 0 && <p className="text-sm text-jigsaw-muted">No offers presented to the client yet.</p>}
              {enquiry.client_offers.map((c) => (
                <div key={c.id} data-testid={`client-offer-row-${c.id}`} className="rounded-md border border-jigsaw-border px-3 py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-jigsaw-navy">{c.supplier_name}{c.property_name ? ` · ${c.property_name}` : ""}{c.terms?.version > 1 ? <span className="ml-2 text-[11px] text-jigsaw-muted">v{c.terms.version}</span> : null}</span>
                  {c.client_decision === "SUPERSEDED" ? (
                    <span className="flex items-center gap-2"><span data-testid={`superseded-${c.id}`} className="text-[12px] text-jigsaw-muted">Replaced by a revised version{c.supersession?.prior_decision === "ACCEPTED" ? " - the earlier acceptance no longer counts for booking" : ""}</span><StatusBadge status="SUPERSEDED" /></span>
                  ) : c.client_decision === "PENDING" ? (
                    <div className="flex gap-2">
                      <Button data-testid={`decide-accept-${c.id}`} size="sm" variant="outline" onClick={() => decideOnBehalf(c.id, "ACCEPTED")}>Accept</Button>
                      <Button data-testid={`decide-decline-${c.id}`} size="sm" variant="outline" className="text-jigsaw-danger" onClick={() => decideOnBehalf(c.id, "DECLINED")}>Decline</Button>
                      <DeclineOfferDialog open={declining === c.id} onOpenChange={(v) => !v && setDeclining(null)} onBehalf onSubmit={(extra) => decideOnBehalf(c.id, "DECLINED", extra)} />
                    </div>
                  ) : (
                    <span className="flex items-center gap-2">
                      <span data-testid={`decided-by-${c.id}`} className="text-[12px] text-jigsaw-muted">
                        {c.decided_by === "JIGSAW" ? `Recorded by Jigsaw on the client's behalf${c.decided_by_name ? ` (${c.decided_by_name})` : ""}`
                          : c.decided_by === "CLIENT" ? `Decided by the client${c.decided_by_name ? ` (${c.decided_by_name})` : ""}`
                          : c.decided_by_name ? `Recorded by ${c.decided_by_name} · workspace at decision time not captured`
                          : "Recorder not captured"}
                      </span>
                      <StatusBadge status={c.client_decision} />
                    </span>
                  )}
                </div>
                <OfferFeedbackSummary offer={c} testId={`offer-feedback-${c.id}`} />
                <OfferInternalNotes enquiryId={id} offer={c} onChanged={load} />
                </div>
              ))}
            </CardContent>
          </Card>
          {enquiry.intake !== undefined && <OptionsLogPanel enquiryId={id} />}
        </TabsContent>

        <TabsContent value="activity" className="space-y-2">
          {enquiry.activity.length === 0 && <p className="text-sm text-jigsaw-muted">No activity recorded yet.</p>}
          {enquiry.activity.map((a) => (
            <div key={a.id} data-testid={`enquiry-activity-${a.id}`} className="flex items-start gap-2 rounded-md border border-jigsaw-border bg-white px-3 py-2 text-sm">
              <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-jigsaw-orange" />
              <div>
                <p className="text-jigsaw-navy">{a.action.replace(/[._]/g, " ")}</p>
                <p className="text-xs text-jigsaw-muted">{new Date(a.created_at).toLocaleString()} · by <ActorStamp event={a} testId={`enquiry-activity-actor-${a.id}`} /></p>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
